import React, { useState, useEffect } from 'react';
import { Navigation } from './Navigation';
import { Megaphone, TrendingUp } from 'lucide-react';
import api from '../utils/api';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Cell
} from 'recharts';

const formatDKK = (val) => Math.round(val).toLocaleString('da-DK');

const COLORS = ['#1E6F7A', '#0288D1', '#2E7D32', '#C77700', '#8B5CF6', '#E91E63', '#FF6F00', '#546E7A'];

const defaultStart = () => {
  const d = new Date();
  d.setMonth(d.getMonth() - 6);
  return d.toISOString().slice(0, 10);
};
const defaultEnd = () => new Date().toISOString().slice(0, 10);

export const MarketingChannels = () => {
  const [activeTab, setActiveTab] = useState('omsaetning');
  const [startDate, setStartDate] = useState(defaultStart);
  const [endDate, setEndDate] = useState(defaultEnd);

  return (
    <div className="min-h-screen bg-[#0a0a0a] text-white p-4 sm:p-6" data-testid="marketing-channels">
      <Navigation />

      {/* Header - stacks on mobile */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
        <div className="flex items-center gap-3">
          <Megaphone className="w-5 h-5 text-[#1E6F7A]" />
          <h1 className="text-xl sm:text-2xl font-bold">Marketingskanaler</h1>
        </div>
        <div className="flex items-center gap-2 sm:gap-3" data-testid="date-picker">
          <input
            type="date"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            className="bg-[#1A1A1A] text-white border border-white/10 rounded-lg px-2 sm:px-3 py-1.5 text-xs sm:text-sm focus:outline-none focus:border-[#1E6F7A] flex-1 min-w-0"
            data-testid="start-date"
          />
          <span className="text-white/40 text-xs sm:text-sm">til</span>
          <input
            type="date"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
            className="bg-[#1A1A1A] text-white border border-white/10 rounded-lg px-2 sm:px-3 py-1.5 text-xs sm:text-sm focus:outline-none focus:border-[#1E6F7A] flex-1 min-w-0"
            data-testid="end-date"
          />
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 mb-6 border-b border-white/10">
        <button
          onClick={() => setActiveTab('omsaetning')}
          className={`px-4 py-2 text-sm font-medium transition-colors border-b-2 -mb-px ${
            activeTab === 'omsaetning'
              ? 'text-[#1E6F7A] border-[#1E6F7A]'
              : 'text-white/50 border-transparent hover:text-white/80'
          }`}
          data-testid="tab-omsaetning"
        >
          Omsætning
        </button>
        <button
          onClick={() => setActiveTab('konvertering')}
          className={`px-4 py-2 text-sm font-medium transition-colors border-b-2 -mb-px flex items-center gap-1.5 ${
            activeTab === 'konvertering'
              ? 'text-[#1E6F7A] border-[#1E6F7A]'
              : 'text-white/50 border-transparent hover:text-white/80'
          }`}
          data-testid="tab-konvertering"
        >
          <TrendingUp className="w-4 h-4" />
          Konvertering
        </button>
      </div>

      {activeTab === 'omsaetning' ? (
        <>
          <KanalDBChart startDate={startDate} endDate={endDate} />
          <DomaineChart startDate={startDate} endDate={endDate} />
        </>
      ) : (
        <KonverteringChart startDate={startDate} endDate={endDate} />
      )}
    </div>
  );
};

const KanalDBChart = ({ startDate, endDate }) => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const res = await api.get(`/marketing/kanaler?start_date=${startDate}&end_date=${endDate}`);
        setData(res.data.data || []);
      } catch (e) {
        console.error('Error fetching marketing kanaler:', e);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [startDate, endDate]);

  const overallAvg = data.length > 0
    ? Math.round(data.reduce((s, d) => s + d.total_db, 0) / data.reduce((s, d) => s + d.antal_ordrer, 0))
    : 0;

  const chartHeight = Math.max(250, data.length * 50);

  return (
    <div className="bg-[#151515] rounded-xl p-4 sm:p-5 border border-white/5" data-testid="kanal-db-chart">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-1 mb-1">
        <h2 className="text-sm font-medium text-white/80">Gns. dækningsbidrag pr. marketingskanal</h2>
        {overallAvg > 0 && <span className="text-xs text-white/40">Samlet gns: {formatDKK(overallAvg)} kr</span>}
      </div>
      <p className="text-xs text-white/30 mb-5">Baseret på ordrer der ikke er canceled eller pending</p>

      {loading ? (
        <div className="h-64 flex items-center justify-center text-white/40">Indlæser...</div>
      ) : data.length === 0 ? (
        <div className="h-64 flex items-center justify-center text-white/40">Ingen data for den valgte periode</div>
      ) : (
        <>
          {/* Chart - responsive margins */}
          <div className="-mx-2 sm:mx-0">
            <ResponsiveContainer width="100%" height={chartHeight}>
              <BarChart data={data} layout="vertical" margin={{ top: 5, right: 10, left: 10, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
                <XAxis type="number" tick={{ fill: 'rgba(255,255,255,0.5)', fontSize: 10 }} tickFormatter={(v) => `${(v / 1000).toFixed(0)}k`} />
                <YAxis type="category" dataKey="kanal" tick={{ fill: 'rgba(255,255,255,0.7)', fontSize: 11 }} width={95} />
                <Tooltip
                  contentStyle={{ background: '#1A1A1A', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 8 }}
                  labelStyle={{ color: 'rgba(255,255,255,0.6)', fontSize: 12, marginBottom: 4 }}
                  formatter={(value, name, props) => {
                    const item = props.payload;
                    return [`${formatDKK(value)} kr (${item.antal_ordrer} ordrer)`, 'Gns. DB'];
                  }}
                />
                <Bar dataKey="gns_db" radius={[0, 4, 4, 0]} barSize={24}>
                  {data.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} fillOpacity={0.85} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>

          {/* Table - cards on mobile, table on desktop */}
          <div className="mt-6">
            {/* Desktop table */}
            <div className="hidden sm:block overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-white/60 border-b border-white/10">
                    <th className="text-left py-2 px-3">Kanal</th>
                    <th className="text-right py-2 px-3">Antal ordrer</th>
                    <th className="text-right py-2 px-3">Gns. DB</th>
                    <th className="text-right py-2 px-3">Total DB</th>
                  </tr>
                </thead>
                <tbody>
                  {data.map((row, idx) => (
                    <tr key={row.kanal} className={`border-b border-white/5 ${idx % 2 === 0 ? 'bg-white/[0.02]' : ''}`}>
                      <td className="py-2 px-3 text-white flex items-center gap-2">
                        <span className="w-2.5 h-2.5 rounded-sm inline-block flex-shrink-0" style={{ background: COLORS[idx % COLORS.length] }}></span>
                        {row.kanal}
                      </td>
                      <td className="py-2 px-3 text-right text-white/70">{row.antal_ordrer}</td>
                      <td className="py-2 px-3 text-right font-medium" style={{ color: COLORS[idx % COLORS.length] }}>{formatDKK(row.gns_db)} kr</td>
                      <td className="py-2 px-3 text-right text-white/70">{formatDKK(row.total_db)} kr</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Mobile cards */}
            <div className="sm:hidden space-y-3">
              {data.map((row, idx) => (
                <div key={row.kanal} className="bg-white/[0.03] rounded-lg p-3 border border-white/5">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="w-2.5 h-2.5 rounded-sm flex-shrink-0" style={{ background: COLORS[idx % COLORS.length] }}></span>
                    <span className="text-sm font-medium text-white">{row.kanal}</span>
                  </div>
                  <div className="grid grid-cols-3 gap-2 text-center">
                    <div>
                      <p className="text-[10px] text-white/40">Ordrer</p>
                      <p className="text-sm text-white/80">{row.antal_ordrer}</p>
                    </div>
                    <div>
                      <p className="text-[10px] text-white/40">Gns. DB</p>
                      <p className="text-sm font-medium" style={{ color: COLORS[idx % COLORS.length] }}>{formatDKK(row.gns_db)}</p>
                    </div>
                    <div>
                      <p className="text-[10px] text-white/40">Total DB</p>
                      <p className="text-sm text-white/80">{formatDKK(row.total_db)}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

const KONV_COLORS = ['#2E7D32', '#1E6F7A', '#0288D1', '#C77700', '#8B5CF6', '#E91E63', '#FF6F00', '#546E7A'];

const KonverteringChart = ({ startDate, endDate }) => {
  const [data, setData] = useState([]);
  const [meta, setMeta] = useState({ total_bookings: 0, total_orders: 0, matched_orders: 0 });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const res = await api.get(`/marketing/konvertering?start_date=${startDate}&end_date=${endDate}`);
        setData(res.data.data || []);
        setMeta({
          total_bookings: res.data.total_bookings || 0,
          total_orders: res.data.total_orders || 0,
          matched_orders: res.data.matched_orders || 0,
        });
      } catch (e) {
        console.error('Error fetching konvertering:', e);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [startDate, endDate]);

  // Only show channels that actually contributed orders in the chart
  const chartData = data.filter(d => d.antal_ordrer > 0);
  const chartHeight = Math.max(260, chartData.length * 50);
  const overallConv = meta.total_bookings > 0
    ? ((meta.matched_orders / meta.total_bookings) * 100).toFixed(1)
    : '0.0';

  return (
    <div className="bg-[#151515] rounded-xl p-4 sm:p-5 border border-white/5" data-testid="konvertering-chart">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-1 mb-1">
        <h2 className="text-sm font-medium text-white/80">Konvertering pr. marketingskanal</h2>
        <div className="flex flex-wrap gap-3 text-xs text-white/50">
          <span>Bookinger: <span className="text-white/80">{meta.total_bookings.toLocaleString('da-DK')}</span></span>
          <span>Ordrer: <span className="text-white/80">{meta.total_orders.toLocaleString('da-DK')}</span></span>
          <span>Matchet: <span className="text-[#2E7D32]">{meta.matched_orders.toLocaleString('da-DK')}</span></span>
          <span>Samlet konv.: <span className="text-[#2E7D32]">{overallConv}%</span></span>
        </div>
      </div>
      <p className="text-xs text-white/30 mb-5">
        Matcher ordrer (hubhus_customer_id) mod bookinger (Booking id) og grupperer efter marketingskanal (ekstrafelter). Ordrer med status canceled/pending er ekskluderet.
      </p>

      {loading ? (
        <div className="h-64 flex items-center justify-center text-white/40">Indlæser...</div>
      ) : chartData.length === 0 ? (
        <div className="h-64 flex items-center justify-center text-white/40">Ingen data for den valgte periode</div>
      ) : (
        <>
          <div className="-mx-2 sm:mx-0">
            <ResponsiveContainer width="100%" height={chartHeight}>
              <BarChart data={chartData} layout="vertical" margin={{ top: 5, right: 10, left: 10, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
                <XAxis type="number" tick={{ fill: 'rgba(255,255,255,0.5)', fontSize: 10 }} />
                <YAxis type="category" dataKey="kanal" tick={{ fill: 'rgba(255,255,255,0.7)', fontSize: 11 }} width={110} />
                <Tooltip
                  contentStyle={{ background: '#1A1A1A', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 8 }}
                  labelStyle={{ color: 'rgba(255,255,255,0.6)', fontSize: 12, marginBottom: 4 }}
                  formatter={(value, name, props) => {
                    const item = props.payload;
                    return [`${value} ordrer (${item.konvertering_pct}% af ${item.antal_bookinger} bookinger)`, 'Konverterede ordrer'];
                  }}
                />
                <Bar dataKey="antal_ordrer" radius={[0, 4, 4, 0]} barSize={24}>
                  {chartData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={KONV_COLORS[index % KONV_COLORS.length]} fillOpacity={0.9} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>

          {/* Table */}
          <div className="mt-6">
            <div className="hidden sm:block overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-white/60 border-b border-white/10">
                    <th className="text-left py-2 px-3">Kanal</th>
                    <th className="text-right py-2 px-3">Bookinger</th>
                    <th className="text-right py-2 px-3">Ordrer</th>
                    <th className="text-right py-2 px-3">Konvertering</th>
                  </tr>
                </thead>
                <tbody>
                  {data.map((row, idx) => (
                    <tr key={row.kanal} className={`border-b border-white/5 ${idx % 2 === 0 ? 'bg-white/[0.02]' : ''}`} data-testid={`konv-row-${row.kanal.replace(/\s+/g, '-').toLowerCase()}`}>
                      <td className="py-2 px-3 text-white flex items-center gap-2">
                        <span className="w-2.5 h-2.5 rounded-sm inline-block flex-shrink-0" style={{ background: row.antal_ordrer > 0 ? KONV_COLORS[idx % KONV_COLORS.length] : '#404040' }}></span>
                        {row.kanal}
                      </td>
                      <td className="py-2 px-3 text-right text-white/70">{row.antal_bookinger.toLocaleString('da-DK')}</td>
                      <td className="py-2 px-3 text-right font-medium" style={{ color: row.antal_ordrer > 0 ? KONV_COLORS[idx % KONV_COLORS.length] : 'rgba(255,255,255,0.3)' }}>{row.antal_ordrer.toLocaleString('da-DK')}</td>
                      <td className="py-2 px-3 text-right text-white/80 font-medium">{row.konvertering_pct}%</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="sm:hidden space-y-3">
              {data.map((row, idx) => (
                <div key={row.kanal} className="bg-white/[0.03] rounded-lg p-3 border border-white/5">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="w-2.5 h-2.5 rounded-sm flex-shrink-0" style={{ background: row.antal_ordrer > 0 ? KONV_COLORS[idx % KONV_COLORS.length] : '#404040' }}></span>
                    <span className="text-sm font-medium text-white">{row.kanal}</span>
                  </div>
                  <div className="grid grid-cols-3 gap-2 text-center">
                    <div>
                      <p className="text-[10px] text-white/40">Bookinger</p>
                      <p className="text-sm text-white/80">{row.antal_bookinger}</p>
                    </div>
                    <div>
                      <p className="text-[10px] text-white/40">Ordrer</p>
                      <p className="text-sm font-medium" style={{ color: row.antal_ordrer > 0 ? KONV_COLORS[idx % KONV_COLORS.length] : 'rgba(255,255,255,0.3)' }}>{row.antal_ordrer}</p>
                    </div>
                    <div>
                      <p className="text-[10px] text-white/40">Konv.</p>
                      <p className="text-sm text-white/80">{row.konvertering_pct}%</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

const DOMAIN_COLORS = ['#C77700', '#8B5CF6', '#E91E63', '#0288D1', '#2E7D32', '#FF6F00', '#1E6F7A', '#546E7A'];

const DomaineChart = ({ startDate, endDate }) => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const res = await api.get(`/marketing/domaener?start_date=${startDate}&end_date=${endDate}`);
        setData(res.data.data || []);
      } catch (e) {
        console.error('Error fetching domaener:', e);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [startDate, endDate]);

  const overallAvg = data.length > 0
    ? Math.round(data.reduce((s, d) => s + d.total_db, 0) / data.reduce((s, d) => s + d.antal_ordrer, 0))
    : 0;

  const chartHeight = Math.max(250, data.length * 50);

  return (
    <div className="bg-[#151515] rounded-xl p-4 sm:p-5 border border-white/5 mt-6" data-testid="domaine-db-chart">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-1 mb-1">
        <h2 className="text-sm font-medium text-white/80">Gns. dækningsbidrag pr. domæne</h2>
        {overallAvg > 0 && <span className="text-xs text-white/40">Samlet gns: {formatDKK(overallAvg)} kr</span>}
      </div>
      <p className="text-xs text-white/30 mb-5">Baseret på hubhus_domain_source — ordrer uden domæne er udeladt</p>

      {loading ? (
        <div className="h-64 flex items-center justify-center text-white/40">Indlæser...</div>
      ) : data.length === 0 ? (
        <div className="h-64 flex items-center justify-center text-white/40">Ingen data for den valgte periode</div>
      ) : (
        <>
          <div className="-mx-2 sm:mx-0">
            <ResponsiveContainer width="100%" height={chartHeight}>
              <BarChart data={data} layout="vertical" margin={{ top: 5, right: 10, left: 10, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
                <XAxis type="number" tick={{ fill: 'rgba(255,255,255,0.5)', fontSize: 10 }} tickFormatter={(v) => `${(v / 1000).toFixed(0)}k`} />
                <YAxis type="category" dataKey="domaine" tick={{ fill: 'rgba(255,255,255,0.7)', fontSize: 11 }} width={95} />
                <Tooltip
                  contentStyle={{ background: '#1A1A1A', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 8 }}
                  labelStyle={{ color: 'rgba(255,255,255,0.6)', fontSize: 12, marginBottom: 4 }}
                  formatter={(value, name, props) => {
                    const item = props.payload;
                    return [`${formatDKK(value)} kr (${item.antal_ordrer} ordrer)`, 'Gns. DB'];
                  }}
                />
                <Bar dataKey="gns_db" radius={[0, 4, 4, 0]} barSize={24}>
                  {data.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={DOMAIN_COLORS[index % DOMAIN_COLORS.length]} fillOpacity={0.85} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>

          <div className="mt-6">
            <div className="hidden sm:block overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-white/60 border-b border-white/10">
                    <th className="text-left py-2 px-3">Domæne</th>
                    <th className="text-right py-2 px-3">Antal ordrer</th>
                    <th className="text-right py-2 px-3">Gns. DB</th>
                    <th className="text-right py-2 px-3">Total DB</th>
                  </tr>
                </thead>
                <tbody>
                  {data.map((row, idx) => (
                    <tr key={row.domaine} className={`border-b border-white/5 ${idx % 2 === 0 ? 'bg-white/[0.02]' : ''}`}>
                      <td className="py-2 px-3 text-white flex items-center gap-2">
                        <span className="w-2.5 h-2.5 rounded-sm inline-block flex-shrink-0" style={{ background: DOMAIN_COLORS[idx % DOMAIN_COLORS.length] }}></span>
                        {row.domaine}
                      </td>
                      <td className="py-2 px-3 text-right text-white/70">{row.antal_ordrer}</td>
                      <td className="py-2 px-3 text-right font-medium" style={{ color: DOMAIN_COLORS[idx % DOMAIN_COLORS.length] }}>{formatDKK(row.gns_db)} kr</td>
                      <td className="py-2 px-3 text-right text-white/70">{formatDKK(row.total_db)} kr</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="sm:hidden space-y-3">
              {data.map((row, idx) => (
                <div key={row.domaine} className="bg-white/[0.03] rounded-lg p-3 border border-white/5">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="w-2.5 h-2.5 rounded-sm flex-shrink-0" style={{ background: DOMAIN_COLORS[idx % DOMAIN_COLORS.length] }}></span>
                    <span className="text-sm font-medium text-white">{row.domaine}</span>
                  </div>
                  <div className="grid grid-cols-3 gap-2 text-center">
                    <div>
                      <p className="text-[10px] text-white/40">Ordrer</p>
                      <p className="text-sm text-white/80">{row.antal_ordrer}</p>
                    </div>
                    <div>
                      <p className="text-[10px] text-white/40">Gns. DB</p>
                      <p className="text-sm font-medium" style={{ color: DOMAIN_COLORS[idx % DOMAIN_COLORS.length] }}>{formatDKK(row.gns_db)}</p>
                    </div>
                    <div>
                      <p className="text-[10px] text-white/40">Total DB</p>
                      <p className="text-sm text-white/80">{formatDKK(row.total_db)}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  );
};
